import {
  PHASES, PRIORIDADES, PRIORITY_LABEL, RHYTHM_LABEL, STUDIO_TOOLS, addDays, daysBetween, monthLabel, phaseOf, phaseStarts,
  pulseBeats, pulseGains, pulseMonths, pulseRhythm, pulseSignals, weekMonth,
  type PulseActionDto, type PulseBeat, type PulseData, type PulseDto, type PulseGains, type PulseSignal, type PulseWeek,
} from '@ads/shared';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import * as Ic from '../components/icons';
import { Actions, useViewMeta } from '../components/Shell';
import { ViewSkeleton } from '../components/Skeleton';
import { dateBR } from '../lib/format';
import { usePulse } from '../lib/queries';
import { useWidth } from '../lib/useWidth';

/* ─────────────────────────────────────────────────────────────────────
   Pulse — a estratégia em movimento.
   Toda semana o ADS grava um retrato do projeto; esta tela compara os
   retratos. Primeiro o ritmo (as semanas com avanço e o que pede atenção),
   depois os quatro sinais vitais, a jornada de construir e colocar em
   prática, o mês a mês, a distância do começo, o plano e o Studio agora,
   e a lista das semanas. Nada de IA: só os números e as datas.
   ───────────────────────────────────────────────────────────────────── */

const MON = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const dm = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
const dMon = (d: string) => `${+d.slice(8, 10)} ${MON[+d.slice(5, 7) - 1]}`;
const weekRange = (w: string) => {
  const e = addDays(w, 6);
  return w.slice(5, 7) === e.slice(5, 7) ? `${+w.slice(8, 10)}–${dMon(e)}` : `${dMon(w)} – ${dMon(e)}`;
};
const num = (x: number) => x.toLocaleString('pt-BR');
const plural = (k: number, one: string, many: string) => `${num(k)} ${k === 1 ? one : many}`;
const toolName = (k: string) => STUDIO_TOOLS.find(t => t.key === k)?.name ?? k;

/** O que avançou numa semana (ou num período), em frases curtas. Só o que subiu; `plus` marca os números como acréscimo. */
function gainParts(g: PulseGains, plus = false): string[] {
  const out: string[] = [];
  const add = (k: number, one: string, many: string) => { if (k > 0) out.push(`${plus ? '+' : ''}${plural(k, one, many)}`); };
  add(g.done, 'ação concluída', 'ações concluídas');
  add(g.pieces, 'peça no Studio', 'peças no Studio');
  add(g.published, 'publicação no ar', 'publicações no ar');
  add(g.evidence, 'evidência', 'evidências');
  add(g.actions, 'ação no plano', 'ações no plano');
  add(g.tools, 'ferramenta concluída', 'ferramentas concluídas');
  if (g.progress > 0) out.push(`estratégia +${g.progress} pts`);
  return out;
}
const sentence = (xs: string[]) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} e ${xs.at(-1)}`);

function derive(p: PulseDto) {
  const weeks = p.weeks;
  const now = weeks.at(-1)!.data;
  const start = weeks[0]!;
  const ago = (weeks.at(-5) ?? start).data;
  return {
    weeks, now, start, ago,
    beats: pulseBeats(weeks),
    rhythm: pulseRhythm(weeks),
    months: pulseMonths(weeks, p.today),
    signals: pulseSignals(p),
    phases: phaseStarts(weeks),
    phase: phaseOf(now),
    recent: pulseGains(ago, now),
  };
}
type D = ReturnType<typeof derive>;

export function Pulse() {
  useViewMeta({ nav: 'pulse', crumb: ['Pulse', 'A estratégia em movimento'], j: 1 });
  const nav = useNavigate();
  const q = usePulse();
  const p = q.data;
  const d = useMemo(() => (p?.weeks.length ? derive(p) : null), [p]);
  const root = useRef<HTMLElement>(null);

  /* os blocos entram conforme aparecem na tela */
  useEffect(() => {
    const el = root.current;
    if (!el || !d) return;
    const io = new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } }), { rootMargin: '0px 0px -6% 0px' });
    el.querySelectorAll('.dx-rv:not(.in)').forEach(x => io.observe(x));
    return () => io.disconnect();
  }, [d]);

  return (
    <section className="view on pl" data-v="pulse" ref={root}>
      <div className="vhead">
        <div>
          <div className="eyebrow"><span className="rule" /><span>Pulse</span><b>A estratégia em movimento</b></div>
          <h1>Pulse</h1>
          <p className="lede">Semana a semana: o que da estratégia foi construído, o que do plano foi colocado em prática e o que o Studio entregou.</p>
        </div>
        {p && (
          <div className="tools">
            <span className="pl-live" title="A semana corrente se atualiza até virar; as passadas ficam como estavam">
              <i aria-hidden="true" />Semana de {dm(p.week)} · medida agora
            </span>
          </div>
        )}
      </div>

      {!p || !d ? (q.isError ? <p className="gd-empty">Não foi possível carregar o Pulse agora.</p> : <ViewSkeleton cards={4} />) : (
        <>
          <Hero p={p} d={d} />
          <Vitals d={d} />

          <Part title="Construir e colocar em prática" lede="Quanto da estratégia estava pronto e quanto do plano já tinha sido feito, a cada semana — e o que chegou no caminho."
            link={['/strategy/w5h2', 'Plano de Ação']}>
            <article className="dx-b dx-rv s12 pl-journey-b">
              <Journey d={d} today={p.today} />
            </article>
          </Part>

          <Part title="Mês a mês e desde o começo" lede={`O crescimento de cada mês e a distância do primeiro retrato, de ${dateBR(d.start.week)}.`}>
            <Months d={d} today={p.today} />
            <StartNow d={d} />
          </Part>

          <Part title="Agora" lede="O plano e o Studio nesta semana: o que venceu, o que vem, o que foi feito e o que foi entregue.">
            <Plan p={p} />
            <Studio p={p} d={d} />
          </Part>

          <Part title="Semana a semana" lede="Cada linha é o retrato de uma semana, gravado quando ela terminou.">
            <WeekLog d={d} />
          </Part>
        </>
      )}

      <Actions>
        <button className="btn-ghost" type="button" onClick={() => nav('/strategy/w5h2')}>Abrir o Plano de Ação</button>
        <button className="btn-primary" type="button" onClick={() => nav('/studio')}>Abrir o Studio<Ic.ArrowRight /></button>
      </Actions>
    </section>
  );
}

/* ── moldura ──────────────────────────────────────────────────────── */

function Part({ title, lede, link, children }: { title: string; lede: string; link?: [string, string]; children: ReactNode }) {
  const nav = useNavigate();
  return (
    <section className="pl-part">
      <header className="pl-part-h dx-rv">
        <div><h2>{title}</h2><p>{lede}</p></div>
        {link && <button type="button" className="dx-more" onClick={() => nav(link[0])}>{link[1]}<Ic.ArrowRight /></button>}
      </header>
      <div className="dx-grid">{children}</div>
    </section>
  );
}

/** Uma dica que segue o ponteiro dentro do gráfico. O conteúdo vem como nós React (texto nunca vira HTML). */
function useTip() {
  const [tip, setTip] = useState<{ x: number; y: number; body: ReactNode } | null>(null);
  const node = tip && (
    <div className="pl-tip" role="status" style={{ '--x': `${tip.x}px`, '--y': `${tip.y}px` } as CSSProperties}>{tip.body}</div>
  );
  return { node, show: (x: number, y: number, body: ReactNode) => setTip({ x, y, body }), hide: () => setTip(null) };
}

/* ── abertura: o ritmo e o que pede atenção ───────────────────────── */

const TONE_ICON: Record<PulseSignal['tone'], ReactNode> = {
  alerta: <Ic.Warning />, atencao: <Ic.Clock />, info: <Ic.Activity />, bom: <Ic.Check />,
};

function Hero({ p, d }: { p: PulseDto; d: D }) {
  const { rhythm, active, of } = d.rhythm;
  const parts = gainParts(d.recent);
  return (
    <section className="pl-hero dx-rv" aria-label="Ritmo">
      <div className="pl-hero-main">
        <p className="dx-k"><Ic.Activity />Ritmo · últimas quatro semanas</p>
        <h2 className={`pl-rhythm r-${rhythm}`}><i aria-hidden="true" />{RHYTHM_LABEL[rhythm]}</h2>
        <p className="pl-sum">
          {rhythm === 'comecando'
            ? `O acompanhamento começou em ${dateBR(d.start.week)}. Com mais algumas semanas, o Pulse mostra o ritmo.`
            : <>{active === of ? `Todas as ${of} semanas` : `${active} de ${of} semanas`} com avanço.{' '}
              {parts.length ? <>No período: <b>{sentence(parts)}</b>.</> : 'Nada mudou no período.'}</>}
        </p>
        <Beats beats={d.beats} weeks={d.weeks} today={p.today} />
      </div>
      <aside className="pl-hero-side" aria-label="O que pede atenção">
        <p className="dx-k">O que pede atenção</p>
        {d.signals.length ? (
          <ul className="pl-signals">
            {d.signals.slice(0, 5).map(s => <li key={s.key} className={`t-${s.tone}`}><span>{TONE_ICON[s.tone]}</span><p>{s.text}</p></li>)}
          </ul>
        ) : <p className="pl-calm">Nada fora do lugar nesta semana.</p>}
        <p className="pl-hero-foot">{plural(p.weeks.length, 'semana registrada', 'semanas registradas')} · desde {dateBR(d.start.week)}</p>
      </aside>
    </section>
  );
}

/** Uma coluna por semana, da primeira até esta: a altura é quanto se avançou. */
function Beats({ beats, weeks, today }: { beats: PulseBeat[]; weeks: PulseWeek[]; today: string }) {
  const [ref, W] = useWidth<HTMLDivElement>();
  const tip = useTip();
  const H = 96, B = 72;
  const n = beats.length;
  const slot = W / Math.max(n, 1);
  const bw = Math.max(4, Math.min(18, slot * .56));
  const max = Math.max(1, ...beats.map(b => b.beat));
  const months = weeks.map((w, i) => ({ i, m: weekMonth(w.week, today) })).filter((x, i, a) => !i || x.m !== a[i - 1]!.m);
  return (
    <div className="pl-beats" ref={ref} onPointerLeave={tip.hide}>
      {W > 0 && (
        <svg width={W} height={H} role="img" aria-label={`Avanço semanal: ${beats.filter(b => b.beat > 0).length} de ${n} semanas com avanço`}>
          <line x1={0} x2={W} y1={B + .5} y2={B + .5} className="pl-base" />
          {beats.map((b, i) => {
            const x = slot * i + (slot - bw) / 2;
            const h = b.beat > 0 ? Math.max(6, (b.beat / max) * (B - 8)) : 0;
            const last = i === n - 1;
            const body = (
              <><b>{last ? 'Esta semana' : `Semana de ${weekRange(b.week)}`}</b>
                <span>{b.first ? 'Primeiro retrato: o ponto de partida' : b.filled ? 'Sem registro — repete a semana anterior' : gainParts(b.gains).length ? sentence(gainParts(b.gains, true)) : 'Sem avanço'}</span></>
            );
            return (
              <g key={b.week} className={`pl-beat${last ? ' now' : ''}${b.filled ? ' filled' : ''}`} style={{ '--i': i } as CSSProperties}
                onPointerEnter={() => tip.show(x + bw / 2, B - h - 6, body)} tabIndex={0} aria-label={`${weekRange(b.week)}: ${b.beat > 0 ? sentence(gainParts(b.gains)) : 'sem avanço'}`}
                onFocus={() => tip.show(x + bw / 2, B - h - 6, body)} onBlur={tip.hide}>
                <rect className="hit" x={slot * i} y={0} width={slot} height={B} />
                {h > 0 ? <path d={`M${x} ${B}V${B - h + 4}a4 4 0 0 1 4 -4h${bw - 8}a4 4 0 0 1 4 4V${B}z`} /> : <circle cx={x + bw / 2} cy={B - 2} r={2} className="zero" />}
              </g>
            );
          })}
          {months.map(({ i, m }) => (
            <text key={m} x={slot * i + slot / 2} y={H - 4} className="pl-tick" textAnchor={i === 0 ? 'start' : 'middle'}>{monthLabel(m)}</text>
          ))}
        </svg>
      )}
      {tip.node}
    </div>
  );
}

/* ── os sinais vitais ─────────────────────────────────────────────── */

function Vitals({ d }: { d: D }) {
  const nav = useNavigate();
  const w = d.weeks;
  const tiles: Array<{ key: string; label: string; icon: ReactNode; value: ReactNode; unit?: string; caption: string; series: number[]; delta: number; deltaLabel: (k: number) => string; start: string; to: string }> = [
    {
      key: 'estrategia', label: 'Estratégia', icon: <Ic.Shield />, value: d.now.strategy.score, unit: '/100',
      caption: `${d.now.strategy.tools} de 10 ferramentas · ${d.now.strategy.progress}% preenchido`,
      series: w.map(x => x.data.strategy.score), delta: d.now.strategy.score - d.ago.strategy.score, deltaLabel: k => `${k > 0 ? '+' : ''}${k} pts`,
      start: `${d.start.data.strategy.score}/100`, to: '/strategy',
    },
    {
      key: 'execucao', label: 'Plano executado', icon: <Ic.CheckSquare />, value: d.now.actions.done, unit: ` de ${d.now.actions.total}`,
      caption: d.now.actions.overdue ? `${plural(d.now.actions.overdue, 'ação com prazo vencido', 'ações com prazo vencido')}` : d.now.actions.total ? `${plural(d.now.actions.total - d.now.actions.done, 'ação aberta', 'ações abertas')}, nenhuma atrasada` : 'O plano ainda não tem ações',
      series: w.map(x => x.data.actions.done), delta: d.recent.done, deltaLabel: k => `${k > 0 ? '+' : ''}${plural(k, 'concluída', 'concluídas')}`,
      start: plural(d.start.data.actions.done, 'concluída', 'concluídas'), to: '/strategy/w5h2',
    },
    {
      key: 'evidencias', label: 'Evidências', icon: <Ic.Clip />, value: d.now.evidence.files,
      caption: `anexadas às ferramentas · ${plural(d.now.evidence.materials, 'material', 'materiais')} no Início`,
      series: w.map(x => x.data.evidence.files), delta: d.recent.evidence, deltaLabel: k => `${k > 0 ? '+' : ''}${num(k)}`,
      start: num(d.start.data.evidence.files), to: '/strategy/gaps',
    },
    {
      key: 'studio', label: 'Peças no Studio', icon: <Ic.Nodes />, value: d.now.studio.pieces,
      caption: d.now.studio.published ? `${plural(d.now.studio.published, 'publicada', 'publicadas')} · no ar` : d.now.studio.pieces ? 'nenhuma publicada ainda' : 'nenhuma peça gerada ainda',
      series: w.map(x => x.data.studio.pieces), delta: d.recent.pieces, deltaLabel: k => `${k > 0 ? '+' : ''}${num(k)}`,
      start: num(d.start.data.studio.pieces), to: '/studio',
    },
  ];
  return (
    <section className="pl-vitals" aria-label="Sinais vitais">
      {tiles.map((t, i) => (
        <button key={t.key} type="button" className={`pl-vital dx-rv v-${t.key}`} style={{ '--d': `${i * 60}ms` } as CSSProperties} onClick={() => nav(t.to)}>
          <span className="pl-vital-h">{t.icon}{t.label}</span>
          <span className="pl-vital-v"><b>{t.value}</b>{t.unit && <em>{t.unit}</em>}</span>
          <span className={`pl-delta${t.delta > 0 ? ' up' : t.delta < 0 ? ' down' : ''}`}>{t.delta ? t.deltaLabel(t.delta) : 'sem mudança'}<i> em 4 semanas</i></span>
          <Spark values={t.series} />
          <span className="pl-vital-c">{t.caption}</span>
          <span className="pl-vital-s">No começo: {t.start}</span>
        </button>
      ))}
    </section>
  );
}

function Spark({ values }: { values: number[] }) {
  const [ref, W] = useWidth<HTMLSpanElement>();
  const H = 40;
  const max = Math.max(1, ...values), min = Math.min(0, ...values);
  const x = (i: number) => (values.length > 1 ? (i / (values.length - 1)) * (W - 8) + 4 : W / 2);
  const y = (v: number) => H - 5 - ((v - min) / (max - min || 1)) * (H - 12);
  const line = values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join('');
  return (
    <span className="pl-spark" ref={ref} aria-hidden="true">
      {W > 0 && values.length > 0 && (
        <svg width={W} height={H}>
          <path d={`${line}L${x(values.length - 1).toFixed(1)} ${H}L${x(0).toFixed(1)} ${H}z`} className="area" />
          <path d={line} className="line" />
          <circle cx={x(values.length - 1)} cy={y(values.at(-1)!)} r={3.5} className="end" />
        </svg>
      )}
    </span>
  );
}

/* ── a jornada: construir e colocar em prática ────────────────────── */

function Journey({ d, today }: { d: D; today: string }) {
  const [ref, W] = useWidth<HTMLDivElement>();
  const tip = useTip();
  const [at, setAt] = useState<number | null>(null);
  const w = d.weeks;
  const n = w.length;
  /* no celular: margens curtas, rótulos da ponta por dentro, só o nome da fase atual */
  const compact = W > 0 && W < 560;
  const L = compact ? 34 : 74, R = compact ? 10 : 118, T = 40, PH = compact ? 170 : 210, LANE = compact ? 26 : 30;
  const H = T + PH + 18 + LANE * 2 + 26;
  const iw = Math.max(0, W - L - R);
  const x = (i: number) => L + (n > 1 ? (i / (n - 1)) * iw : iw / 2);
  const y = (v: number) => T + PH - (v / 100) * PH;
  const strat = w.map(k => k.data.strategy.progress);
  const plan = w.map(k => (k.data.actions.total ? Math.round((k.data.actions.done / k.data.actions.total) * 100) : null));
  const gains = w.map((k, i) => (i ? pulseGains(w[i - 1]!.data, k.data) : null));
  const path = (vs: Array<number | null>) => vs.map((v, i) => (v == null ? '' : `${i && vs[i - 1] != null ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`)).join('');
  const firstPlan = plan.findIndex(v => v != null);
  /* as fases: um marco onde cada uma começou, com o nome no alto (em duas alturas, sem encavalar); a fase atual ganha um fundo */
  const marks: Array<{ k: string; label: string; x: number; row: number; on: boolean }> = [];
  for (const [k, label] of PHASES) {
    const i = d.phases[k] ? w.findIndex(x => x.week === d.phases[k]) : -1;
    if (i < 0) continue;
    const mx = i ? (x(i - 1) + x(i)) / 2 : L;
    const prev = marks.at(-1);
    const row = prev && prev.row === 0 && mx - (prev.x + prev.label.length * 6.1 + 18) < 0 ? 1 : 0;
    marks.push({ k, label, x: mx, row, on: k === d.phase });
  }
  const cur = marks.find(m => m.on);
  const evMax = Math.max(1, ...gains.map(g => Math.max(0, g?.evidence ?? 0)));
  const laneY = T + PH + 18;
  const pick = (px: number) => Math.max(0, Math.min(n - 1, Math.round(((px - L) / Math.max(1, iw)) * (n - 1))));
  const show = (i: number) => {
    setAt(i);
    const g = gains[i];
    const k = w[i]!.data;
    tip.show(x(i), T + 6, (
      <><b>{i === n - 1 ? 'Esta semana' : `Semana de ${weekRange(w[i]!.week)}`}</b>
        <span className="row"><i className="pl-key s" />Estratégia <strong>{k.strategy.progress}%</strong></span>
        <span className="row"><i className="pl-key p" />Plano {plan[i] == null ? <strong>ainda não existia</strong> : <strong>{plan[i]}% · {k.actions.done} de {k.actions.total}</strong>}</span>
        {g && (g.evidence > 0 || g.pieces > 0) && <span className="more">{sentence(gainParts({ ...g, progress: 0, actions: 0, tools: 0, done: 0 }, true))}</span>}
      </>
    ));
  };
  const endS = strat.at(-1)!, endP = plan.at(-1);
  /* os rótulos da ponta: se ficarem perto, um sobe e o outro desce */
  let yS = y(endS) + 4, yP = endP != null ? y(endP) + 4 : 0;
  if (endP != null && Math.abs(yS - yP) < 16) { if (yS <= yP) { yS -= 8; yP += 8; } else { yS += 8; yP -= 8; } }
  return (
    <div className="pl-journey" ref={ref}>
      <div className="pl-legend" aria-hidden="true">
        <span><i className="pl-key s" />Estratégia construída{compact && <b>{endS}%</b>}</span>
        <span><i className="pl-key p" />Plano concluído{compact && endP != null && <b>{endP}%</b>}</span>
        <span><i className="pl-key e" />Evidências novas</span>
        <span><i className="pl-key st" />Peças do Studio</span>
      </div>
      <div className="pl-journey-plot" onPointerMove={e => { const r = e.currentTarget.getBoundingClientRect(); show(pick(e.clientX - r.left)); }}
        onPointerLeave={() => { setAt(null); tip.hide(); }}>
        {W > 0 && (
          <svg width={W} height={H} role="img"
            aria-label={`De ${dateBR(w[0]!.week)} até esta semana, a estratégia foi de ${strat[0]}% a ${endS}% construída${endP != null ? ` e o plano chegou a ${endP}% concluído` : ''}.`}>
            {cur && <rect className="pl-now-band" x={cur.x} y={T} width={Math.max(0, L + iw - cur.x)} height={PH} />}
            {marks.map(m => {
              const row = compact ? 0 : m.row;
              const right = compact && m.x > L + iw * .55;
              return (
                <g key={m.k} className={`pl-mark${m.on ? ' on' : ''}`}>
                  {m.x > L && <line x1={m.x + .5} x2={m.x + .5} y1={T - (row ? 34 : 20)} y2={T + PH} />}
                  {(!compact || m.on) && (
                    <text x={right ? L + iw : m.x + (m.x > L ? 7 : 0)} y={T - (row ? 24 : 10)} textAnchor={right ? 'end' : 'start'}>{m.label}</text>
                  )}
                </g>
              );
            })}
            {[0, 25, 50, 75, 100].map(v => (
              <g key={v}>
                <line x1={L} x2={L + iw} y1={y(v) + .5} y2={y(v) + .5} className={v ? 'pl-grid' : 'pl-base'} />
                {v % 50 === 0 && <text x={L - 10} y={y(v) + 4} textAnchor="end" className="pl-tick">{v}%</text>}
              </g>
            ))}
            <path d={`${path(strat)}L${x(n - 1)} ${y(0)}L${x(0)} ${y(0)}z`} className="pl-area s" />
            <path d={path(strat)} className="pl-line s" />
            <path d={path(plan)} className="pl-line p" />
            {firstPlan >= 0 && <circle cx={x(firstPlan)} cy={y(plan[firstPlan]!)} r={4} className="pl-dot p" />}
            <circle cx={x(n - 1)} cy={y(endS)} r={4} className="pl-dot s" />
            {endP != null && <circle cx={x(n - 1)} cy={y(endP)} r={4.5} className="pl-dot p" />}
            {!compact && (
              <>
                <text x={x(n - 1) + 12} y={yS} className="pl-end">{endS}% <tspan>estratégia</tspan></text>
                {endP != null && <text x={x(n - 1) + 12} y={yP} className="pl-end">{endP}% <tspan>plano</tspan></text>}
              </>
            )}

            {/* as faixas de baixo: evidências (colunas) e peças do Studio (pontos) */}
            {!compact && <text x={L - 10} y={laneY + LANE / 2 + 4} textAnchor="end" className="pl-tick">Evidências</text>}
            <line x1={L} x2={L + iw} y1={laneY + LANE - .5} y2={laneY + LANE - .5} className="pl-grid" />
            {gains.map((g, i) => g && g.evidence > 0 && (
              <rect key={i} className="pl-ev" x={x(i) - 3} width={6} rx={2} y={laneY + LANE - 1 - Math.max(3, (g.evidence / evMax) * (LANE - 6))} height={Math.max(3, (g.evidence / evMax) * (LANE - 6))} />
            ))}
            {!compact && <text x={L - 10} y={laneY + LANE + LANE / 2 + 4} textAnchor="end" className="pl-tick">Studio</text>}
            <line x1={L} x2={L + iw} y1={laneY + LANE * 2 - .5} y2={laneY + LANE * 2 - .5} className="pl-grid" />
            {gains.map((g, i) => g && g.pieces > 0 && Array.from({ length: Math.min(g.pieces, 4) }, (_, k) => (
              <circle key={`${i}-${k}`} className="pl-piece" cx={x(i)} cy={laneY + LANE * 2 - 8 - k * 9} r={4} />
            )))}
            {w.map((k, i) => {
              const m = weekMonth(k.week, today);
              if (i && weekMonth(w[i - 1]!.week, today) === m) return null;
              return <text key={k.week} x={x(i)} y={H - 6} textAnchor={i === 0 ? 'start' : 'middle'} className="pl-tick">{monthLabel(m, !compact && (i === 0 || m.endsWith('-01')))}</text>;
            })}
            {at != null && <line x1={x(at) + .5} x2={x(at) + .5} y1={T} y2={laneY + LANE * 2} className="pl-cross" />}
          </svg>
        )}
        {tip.node}
      </div>
    </div>
  );
}

/* ── mês a mês ────────────────────────────────────────────────────── */

function Months({ d, today }: { d: D; today: string }) {
  const cols: Array<[keyof PulseGains, string, (k: number) => string]> = [
    ['progress', 'Estratégia', k => `+${k} pts`],
    ['done', 'Ações concluídas', k => `+${k}`],
    ['evidence', 'Evidências', k => `+${k}`],
    ['pieces', 'Peças', k => `+${k}`],
  ];
  const max = Object.fromEntries(cols.map(([k]) => [k, Math.max(1, ...d.months.map(m => m.gains[k]))])) as Record<keyof PulseGains, number>;
  const months = d.months.slice(-6);
  return (
    <article className="dx-b dx-rv s7">
      <header className="dx-b-h"><h3><Ic.Bars />Crescimento mês a mês</h3><span className="dx-stat"><b>{months.length}</b>{months.length === 1 ? 'mês' : 'meses'}</span></header>
      <div className="pl-months" role="table" aria-label="Crescimento por mês">
        <div className="r h" role="row"><span role="columnheader">Mês</span>{cols.map(([k, label]) => <span key={k} role="columnheader">{label}</span>)}</div>
        {months.map(m => (
          <div key={m.month} className={`r${m.partial ? ' cur' : ''}`} role="row">
            <span role="rowheader"><b>{monthLabel(m.month)}</b>{m.partial ? <em>até {dm(today)}</em> : <em>{m.month.slice(0, 4)}</em>}</span>
            {cols.map(([k, , fmt]) => {
              const v = Math.max(0, m.gains[k]);
              return (
                <span key={k} role="cell" className={v ? '' : 'zero'}>
                  <b>{v ? fmt(v) : '—'}</b>
                  <i style={{ '--v': `${(v / max[k]) * 100}%` } as CSSProperties} />
                </span>
              );
            })}
          </div>
        ))}
      </div>
      <p className="dx-foot">O último retrato de cada mês contra o do mês anterior; a semana conta no mês em que tem mais dias.</p>
    </article>
  );
}

/* ── do começo até aqui ───────────────────────────────────────────── */

function StartNow({ d }: { d: D }) {
  const a = d.start.data, b = d.now;
  const n1 = (x: number) => x.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const rows: Array<{ label: string; from: number | null; to: number | null; max: number; fmt: (x: number) => string; unit?: string }> = [
    { label: 'Score do DNA', from: a.strategy.score, to: b.strategy.score, max: 100, fmt: num, unit: 'de 100' },
    { label: 'Ferramentas concluídas', from: a.strategy.tools, to: b.strategy.tools, max: 10, fmt: num, unit: 'de 10' },
    { label: 'Maturidade (Mapa de Gaps)', from: a.strategy.maturity, to: b.strategy.maturity, max: 5, fmt: n1, unit: 'de 5' },
    { label: 'Ações concluídas', from: a.actions.done, to: b.actions.done, max: Math.max(1, b.actions.total), fmt: num, unit: `de ${b.actions.total}` },
    { label: 'Evidências', from: a.evidence.files, to: b.evidence.files, max: Math.max(1, b.evidence.files, a.evidence.files), fmt: num },
    { label: 'Peças no Studio', from: a.studio.pieces, to: b.studio.pieces, max: Math.max(1, b.studio.pieces, a.studio.pieces), fmt: num },
  ];
  return (
    <article className="dx-b dx-rv s5">
      <header className="dx-b-h"><h3><Ic.Target />Do começo até aqui</h3><span className="dx-stat"><b>{d.weeks.length}</b>semanas</span></header>
      <ul className="pl-dumb">
        {rows.map(r => {
          const from = r.from ?? r.to, to = r.to ?? r.from;
          if (from == null || to == null) return <li key={r.label} className="none"><span className="l">{r.label}</span><span className="v">ainda sem avaliação</span></li>;
          const fx = (from / r.max) * 100, tx = (to / r.max) * 100;
          return (
            <li key={r.label}>
              <span className="l">{r.label}</span>
              <span className="v">{r.from == null ? '—' : r.fmt(from)} <i>→</i> <b>{r.fmt(to)}</b>{r.unit && <em> {r.unit}</em>}</span>
              <span className="t" aria-hidden="true">
                <i className="seg" style={{ left: `${Math.min(fx, tx)}%`, width: `${Math.abs(tx - fx)}%` }} />
                {r.from != null && <i className="a" style={{ left: `${fx}%` }} />}
                <i className="b" style={{ left: `${tx}%` }} />
              </span>
            </li>
          );
        })}
      </ul>
      <p className="dx-foot">○ primeiro retrato, em {dateBR(d.start.week)} · ● esta semana</p>
    </article>
  );
}

/* ── o plano agora ────────────────────────────────────────────────── */

function Plan({ p }: { p: PulseDto }) {
  const nav = useNavigate();
  const a = p.actions;
  const total = PRIORIDADES.reduce((s, k) => s + a.byPriority[k].total, 0);
  const until = (w: string) => {
    const k = daysBetween(p.today, w);
    return k === 0 ? 'hoje' : k === 1 ? 'amanhã' : `em ${k} dias`;
  };
  const Row = ({ x, kind }: { x: PulseActionDto; kind: 'late' | 'next' | 'done' }) => (
    <li className={kind}>
      <span className="d">
        {kind === 'done' ? <><Ic.Check />{dm(x.doneAt)}</> : <>{dm(x.when)}<em>{kind === 'late' ? `há ${plural(daysBetween(x.when, p.today), 'dia', 'dias')}` : until(x.when)}</em></>}
      </span>
      <span className="w">{x.what}<em>{x.who || 'sem responsável'}</em></span>
      <span className={`pa-chip p-${x.priority}`}>{PRIORITY_LABEL[x.priority]}</span>
    </li>
  );
  return (
    <article className="dx-b dx-rv s7">
      <header className="dx-b-h">
        <h3><Ic.CheckSquare />O plano</h3>
        <button type="button" className="dx-more" onClick={() => nav('/strategy/w5h2')}>Abrir o plano<Ic.ArrowRight /></button>
      </header>
      {!total ? (
        <div className="dx-empty"><p>O Plano de Ação ainda não tem ações. É por ele que o Pulse acompanha a execução.</p>
          <button type="button" className="addlink" onClick={() => nav('/strategy/w5h2')}>Montar o plano<Ic.ArrowRight /></button></div>
      ) : (
        <>
          <ul className="pl-prio">
            {PRIORIDADES.map(k => {
              const b = a.byPriority[k];
              return (
                <li key={k} className={`p-${k}`}>
                  <span className="l">{PRIORITY_LABEL[k]}</span>
                  <span className="t"><i style={{ '--v': `${b.total ? (b.done / b.total) * 100 : 0}%` } as CSSProperties} /></span>
                  <span className="n"><b>{b.done}</b> de {b.total}</span>
                </li>
              );
            })}
          </ul>
          {a.overdue.length > 0 && <div className="dx-sub"><p className="dx-k pl-k-late">Prazo vencido</p><ul className="pl-acts">{a.overdue.slice(0, 3).map(x => <Row key={x.id} x={x} kind="late" />)}</ul></div>}
          <div className="dx-sub">
            <p className="dx-k">Próximas entregas</p>
            {a.next.length ? <ul className="pl-acts">{a.next.slice(0, 4).map(x => <Row key={x.id} x={x} kind="next" />)}</ul>
              : <p className="pl-none">Nenhuma ação aberta com prazo à frente.</p>}
          </div>
          {a.done.length > 0 && <div className="dx-sub"><p className="dx-k">Concluídas por último</p><ul className="pl-acts">{a.done.slice(0, 3).map(x => <Row key={x.id} x={x} kind="done" />)}</ul></div>}
        </>
      )}
    </article>
  );
}

/* ── o Studio agora ───────────────────────────────────────────────── */

function Studio({ p, d }: { p: PulseDto; d: D }) {
  const nav = useNavigate();
  const s = d.now.studio;
  const cats: Array<[keyof PulseData['studio'], string]> = [['estrategia', 'Estratégia'], ['presenca', 'Presença digital'], ['narrativa', 'Narrativa']];
  const perWeek = d.weeks.map((w, i) => (i ? Math.max(0, w.data.studio.pieces - d.weeks[i - 1]!.data.studio.pieces) : 0));
  const pmax = Math.max(1, ...perWeek);
  return (
    <article className="dx-b dx-rv s5">
      <header className="dx-b-h">
        <h3><Ic.Nodes />Studio</h3>
        <button type="button" className="dx-more" onClick={() => nav('/studio')}>Abrir o Studio<Ic.ArrowRight /></button>
      </header>
      {!s.pieces ? (
        <div className="dx-empty"><p>Nenhuma peça gerada ainda. Cada entrega do Studio parte do DNA consolidado.</p>
          <button type="button" className="addlink" onClick={() => nav('/studio')}>Gerar a primeira<Ic.ArrowRight /></button></div>
      ) : (
        <>
          <div className="pl-studio-top">
            <span className="pl-big"><b>{s.pieces}</b>{s.pieces === 1 ? 'peça' : 'peças'}</span>
            <span className="pl-big"><b>{s.published}</b>no ar</span>
            <span className="pl-weeks" aria-label="Peças por semana">
              {perWeek.map((k, i) => <i key={d.weeks[i]!.week} title={`${weekRange(d.weeks[i]!.week)}: ${plural(k, 'peça', 'peças')}`} style={{ '--v': `${(k / pmax) * 100}%` } as CSSProperties} className={k ? '' : 'z'} />)}
            </span>
          </div>
          <p className="pl-cats">{cats.map(([k, l]) => <span key={k}><b>{s[k]}</b>{l}</span>)}</p>
          <div className="dx-sub">
            <p className="dx-k">As mais recentes</p>
            <ul className="pl-pieces">
              {p.pieces.map(x => (
                <li key={x.id}>
                  <button type="button" onClick={() => nav(`/pecas/${x.id}`)}>
                    <span className="w">{x.title}<em>{toolName(x.tool)} · {dateBR(x.createdAt.slice(0, 10))}</em></span>
                    {x.published ? <span className="pl-live-b">No ar</span> : <Ic.ChevronRight />}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <p className="dx-foot">Cada peça parte do DNA consolidado e do design system da organização.</p>
        </>
      )}
    </article>
  );
}

/* ── semana a semana ──────────────────────────────────────────────── */

function WeekLog({ d }: { d: D }) {
  const [all, setAll] = useState(false);
  const rows = [...d.beats].reverse();
  const max = Math.max(1, ...d.beats.map(b => b.beat));
  const shown = all ? rows : rows.slice(0, 6);
  return (
    <article className="dx-b dx-rv s12">
      <ol className="pl-log">
        {shown.map((b, i) => {
          const parts = gainParts(b.gains, true);
          return (
            <li key={b.week} className={b.filled ? 'filled' : ''}>
              <span className="wk"><b>{weekRange(b.week)}</b>{i === 0 ? <em>esta semana</em> : b.first ? <em>primeiro retrato</em> : null}</span>
              <span className="bt" aria-hidden="true"><i style={{ '--v': `${(b.beat / max) * 100}%` } as CSSProperties} /></span>
              <span className="gs">
                {b.first ? <span className="g start">O ponto de partida do acompanhamento</span>
                  : b.filled ? <span className="g none">Sem registro nesta semana — repete a anterior</span>
                  : parts.length ? parts.map(x => <span key={x} className="g">{x}</span>)
                  : <span className="g none">Sem avanço</span>}
              </span>
            </li>
          );
        })}
      </ol>
      {rows.length > 6 && (
        <button type="button" className="addlink pl-all" onClick={() => setAll(v => !v)}>
          {all ? 'Mostrar só as últimas seis' : `Ver as ${rows.length} semanas`}<Ic.ChevronDown style={{ transform: all ? 'rotate(180deg)' : undefined }} />
        </button>
      )}
    </article>
  );
}
